/** @jsxRuntime classic */
/** @jsx jsx */
import { jsx } from '@emotion/core';
import React, {
  useMemo,
  useRef,
  useState,
  useEffect,
  Fragment,
  useCallback,
} from 'react';
import {
  Input,
  message,
  Button,
  Divider,
  Tooltip,
  Checkbox,
  Spin,
  Empty,
  Typography,
} from 'antd';
import { IntlShape, useIntl } from 'react-intl';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ThemedStyleFn, useStyles } from '~src/hooks/useStyles';
import { useForm } from 'react-hook-form';
import isEmail from 'validator/es/lib/isEmail';
import { uniq } from 'lodash';
import debounce from 'lodash/debounce';
import {
  getShareMessage,
  postShareKeys,
  postShareMessage,
  updateShareKey,
  patchInviteMessages,
  getInviteMessagePreview,
} from '~src/services/api/shares';
import { queryCache, useMutation, useQuery } from 'react-query';
import { QueryKeys } from '~src/hooks/api-hooks/queryKeys';
import { appLog } from '~src/utils/log';
import { getShareStatusVisualIntl } from '~src/utils/shareUtils/useShareKeysStatusUtils';

const { TextArea } = Input;

type InviteModalMode = 'invite' | 'edit-resend';

interface InviteTabProps {
  mode?: InviteModalMode;
  initialShares?: any[] | any;
  projectId: number;
  tenderId: number;
  tenderCompleted: boolean;
  onSendClick: () => void;
  onCanSendChange: (canSend: boolean) => void;
  onSendingChange: (isSending: boolean) => void;
  isModalVisible: boolean;
}

type EmailStatus = 'idle' | 'sending' | 'success' | 'error';
type RowState = 'clean' | 'dirty' | 'new';
type LockedReason = 'claimed' | 'rejected' | 'deleted' | undefined;

interface RecipientRow {
  id: string; // share_key_id
  email: string;
  shareKeyTitle: string;

  //keep originals to detect dirty state reliably
  originalEmail?: string;
  originalShareKeyTitle?: string;

  status: EmailStatus;
  rowState: RowState;
  canEdit: boolean;
  canResend: boolean;
  lockedReason?: LockedReason;
  isDuplicate?: boolean;
  shareStatus?: {
    icon: React.ReactNode;
    color: string;
    text: string;
  };
}

const STATUS_META: Record<
  EmailStatus,
  { icon: any; color: string; tip: string }
> = {
  idle: { icon: ['far', 'stopwatch'], color: '#fa8c16', tip: 'Awaiting' },
  sending: { icon: ['far', 'stopwatch'], color: '#1677ff', tip: 'Sending...' },
  success: { icon: ['fad', 'check-circle'], color: '#52c41a', tip: 'Sent' },
  error: { icon: ['far', 'ban'], color: '#ff4d4f', tip: 'Failed' },
};

/* INVITE MODE HELPERS */

const createInviteRow = (email: string): RecipientRow => ({
  id: `${Date.now()}-${Math.random()}`,
  email,
  shareKeyTitle: '',
  originalEmail: email,
  originalShareKeyTitle: '',
  status: 'idle',
  rowState: 'new',
  canEdit: true,
  canResend: true,
  lockedReason: undefined,
  isDuplicate: undefined,
  shareStatus: undefined,
});

const parseEmailsFromText = (text: string): string[] =>
  text
    .split(/[\s,;]+/)
    .map((p) => p.replace(/[<>]/g, '').trim())
    .filter(Boolean);

/* EDIT & RESEND MODE HELPERS */
const createRowFromShare = (intl: IntlShape, shareLike: any): RecipientRow => {
  const s = shareLike?.dbShare ?? shareLike ?? {};

  const email = s.share_key_email || '';
  const title = s.share_key_title || '';
  const shareKeyId =
    s.share_key_id ||
    shareLike?.shareKey ||
    shareLike?.id ||
    `${email}-${Date.now()}`;

  const state = s.share_key_state ?? 0;
  const claimedAt = s.share_key_claimed_at ?? 0;

  const isClaimed = !!s.share_key_is_claimed || claimedAt > 0;
  const isRejected = state === 5; // ResourceState.REJECTED
  const isDeleted = state === 9; // ResourceState.DELETED

  let lockedReason: LockedReason;
  let canEdit = true;
  let canResend = true;

  if (isClaimed) {
    // Claimed: cannot edit email, but resend is allowed
    canEdit = false;
    canResend = false;
    lockedReason = 'claimed';
  }

  if (isRejected || isDeleted) {
    // Rejected / Deleted: cannot edit and cannot resend
    canEdit = false;
    canResend = false;
    lockedReason = isDeleted ? 'deleted' : 'rejected';
  }

  const shareStatus = getShareStatusVisualIntl(intl, s as any);

  return {
    id: shareKeyId,
    email,
    shareKeyTitle: title,
    originalEmail: email,
    originalShareKeyTitle: title,
    status: 'idle',
    rowState: 'clean',
    canEdit,
    canResend,
    lockedReason,
    shareStatus,
  };
};

export const InviteTab: React.FC<InviteTabProps> = ({
  mode = 'invite',
  initialShares,
  projectId,
  tenderId,
  tenderCompleted,
  onSendClick,
  onCanSendChange,
  onSendingChange,
  isModalVisible,
}) => {
  const intl = useIntl();
  const t = (id: string, def: string) =>
    intl.formatMessage({ id, defaultMessage: def });
  const styles = useStyles(styleFn);
  const isEditResendMode = mode === 'edit-resend';

  // Invitation edit state
  const [isInvitationEditing, setIsInvitationEditing] = useState(false);
  const [isInvitationDirty, setIsInvitationDirty] = useState(false);
  const [isInvitationLoading] = useState(false);
  const [isInvitationSaving, setIsInvitationSaving] = useState(false);

  // Recipients state
  const [rows, setRows] = useState<RecipientRow[]>([]);
  const [selectedRowIds, setSelectedRowIds] = useState<string[]>([]);
  const [inputEmails, setInputEmails] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [savingRowIds, setSavingRowIds] = useState<Record<string, boolean>>({});
  const [skipDuplicates, setSkipDuplicates] = useState(true);
  const timersRef = useRef<number[]>([]);

  // get invite message mutation
  const { data: shareMainMessage, isLoading: isShareMessageLoading } = useQuery(
    ['share-invite-message', projectId, tenderId],
    () => getShareMessage({ projectId, tenderId }),
    {
      enabled: !!projectId && !!tenderId && !!isModalVisible,
    }
  );

  // Invitation template form
  const sharesForm = useForm<{ title: string; message: string }>({
    defaultValues: {
      title: shareMainMessage?.title || '',
      message: shareMainMessage?.description || '',
    },
  });

  const invitationTitle = sharesForm.watch('title');
  const invitationMessage = sharesForm.watch('message');

  // Preview state
  const [previewHtml, setPreviewHtml] = useState('');
  const [previewLoading, setPreviewLoading] = useState(false);

  // Debounced preview fetch
  const fetchPreview = useCallback(
    debounce(async (t: string, m: string) => {
      if (!t && !m) {
        setPreviewHtml('');
        return;
      }

      try {
        setPreviewLoading(true);
        const html = await getInviteMessagePreview({
          projectId,
          tenderId,
          title: t,
          message: m,
        });
        setPreviewHtml(html);
      } catch (error) {
        console.error('Failed to fetch preview:', error);
      } finally {
        setPreviewLoading(false);
      }
    }, 500),
    [projectId, tenderId]
  );

  useEffect(() => {
    fetchPreview(invitationTitle, invitationMessage);
  }, [invitationTitle, invitationMessage, fetchPreview]);

  // Post new invitation message mutation
  const [
    doPostInvitationMessage,
    { isLoading: isPostInvitationMessageLoading },
  ] = useMutation(postShareMessage, {
    onSuccess: () => {
      queryCache.invalidateQueries([
        'share-invite-message',
        projectId,
        tenderId,
      ]);
      message.success(
        t('invite-share-success', 'Invitations posted successfully.')
      );
    },
    onError: (error, item) => {
      message.error(
        t('invite-share-edit-failed', 'Failed to post invitation message')
      );
      appLog('share error', error, item);
    },
  });

  // Invite (create) mutation
  const [doPostShareKeys, { isLoading: isPostLoading }] = useMutation(
    postShareKeys,
    {
      onSuccess: () => {
        queryCache.invalidateQueries([QueryKeys.tenderAdminSharedTender]);
        message.success(t('invite-share-success', 'Invitations sent'));
      },
      onError: (error, item) => {
        message.error(
          t('invite-share-edit-failed', 'Failed to send invitations')
        );
        appLog('share error', error, item);
        setRows((prev) =>
          prev.map((r) => ({
            ...r,
            status: 'error',
          }))
        );
      },
    }
  );

  // Edit & resend mode -> PATCH invite_messages (queue + trigger send)
  const [
    doPatchInviteMessages,
    { isLoading: isPatchInviteMessagesLoading },
  ] = useMutation(patchInviteMessages, {
    onSuccess: () => {
      queryCache.invalidateQueries([QueryKeys.tenderAdminSharedTender]);
      message.success(
        t('invite-resend-success', 'Invitations queued and sent')
      );
    },
    onError: (error, item) => {
      message.error(
        t('invite-share-edit-failed', 'Failed to resend invitations')
      );
      appLog('patch-invite-messages error', error, item);
    },
  });

  const invalidRows = useMemo(
    () =>
      rows.filter(
        (r) =>
          !isEmail(r.email) ||
          r.lockedReason === 'rejected' ||
          r.lockedReason === 'deleted'
      ),
    [rows]
  );

  const hasInvitationContent = () =>
    invitationTitle.trim().length > 0 && invitationMessage.trim().length > 0;

  const hasResendableRows = useMemo(() => rows.some((r) => r.canResend), [
    rows,
  ]);

  // For invite mode: old logic; for edit mode: depends on selected checkboxes
  const hasSelectedResendableRows = useMemo(
    () =>
      rows.some(
        (r) => r.canResend && isEmail(r.email) && selectedRowIds.includes(r.id)
      ),
    [rows, selectedRowIds]
  );

  // in edit-resend mode, block sending if any selected row is "dirty"
  const hasSelectedDirtyRows = useMemo(
    () =>
      isEditResendMode &&
      rows.some(
        (r) =>
          selectedRowIds.includes(r.id) && r.rowState === 'dirty' && r.canEdit
      ),
    [isEditResendMode, rows, selectedRowIds]
  );

  const hasClaimedDirtyRows = useMemo(
    () =>
      isEditResendMode &&
      rows.some(
        (r) =>
          selectedRowIds.includes(r.id) && r.rowState === 'dirty' && r.canEdit
      ),
    [isEditResendMode, rows, selectedRowIds]
  );

  const baseCanSend = isEditResendMode
    ? hasSelectedResendableRows &&
      hasInvitationContent() &&
      !hasSelectedDirtyRows
    : rows.length > 0 &&
      invalidRows.length === 0 &&
      hasInvitationContent() &&
      hasResendableRows;

  const canSend =
    baseCanSend &&
    !isSending &&
    !isInvitationEditing &&
    !isInvitationDirty &&
    !isInvitationLoading &&
    !isInvitationSaving &&
    !tenderCompleted;

  // Notify parent about canSend changes
  useEffect(() => {
    onCanSendChange(canSend);
  }, [canSend, onCanSendChange]);

  // Notify parent about isSending changes
  useEffect(() => {
    onSendingChange(isSending || isPostLoading || isPatchInviteMessagesLoading);
  }, [isSending, isPostLoading, isPatchInviteMessagesLoading, onSendingChange]);

  const updateRow = (id: string, patch: Partial<RecipientRow>) => {
    setRows((prev) =>
      prev.map((row) => {
        if (row.id !== id) return row;

        if (!row.canEdit && (patch.email || patch.shareKeyTitle)) {
          return row;
        }

        const next: RecipientRow = { ...row, ...patch };

        // Compute dirty state based on originals (more reliable than patch presence)
        const nextEmail =
          typeof patch.email === 'string' ? patch.email : row.email;
        const nextTitle =
          typeof patch.shareKeyTitle === 'string'
            ? patch.shareKeyTitle
            : row.shareKeyTitle;

        if (row.rowState !== 'new') {
          const isDirty =
            (row.originalEmail ?? row.email) !== nextEmail ||
            (row.originalShareKeyTitle ?? row.shareKeyTitle) !== nextTitle;

          next.rowState = isDirty ? 'dirty' : 'clean';
        } else {
          // invite mode rows stay "new"
          next.rowState = 'new';
        }

        // Recalculate isDuplicate when email changes
        if (typeof patch.email === 'string') {
          const normalizedNextEmail = nextEmail.toLowerCase().trim();
          const isDuplicate = prev.some(
            (r) =>
              r.id !== id && // Don't compare with self
              r.email.toLowerCase().trim() === normalizedNextEmail
          );
          next.isDuplicate = isDuplicate;
        }

        return next;
      })
    );
  };

  // save a single edited row (edit-resend mode)
  const handleSaveRow = async (row: RecipientRow) => {
    if (!projectId) {
      message.error('Missing project id');
      return;
    }

    const numericTenderId = Number(tenderId);
    if (!numericTenderId) {
      message.error('Missing tender id');
      return;
    }

    if (!row.canEdit) return;

    if (!isEmail(row.email)) {
      message.error(t('invite-invalid-email', 'Invalid'));
      return;
    }

    if (row.lockedReason === 'rejected' || row.lockedReason === 'deleted') {
      message.error(
        t('invite-share-edit-failed', 'Cannot edit this recipient')
      );
      return;
    }

    setSavingRowIds((prev) => ({ ...prev, [row.id]: true }));

    // Optional: show a quick visual that we are saving
    setRows((prev) =>
      prev.map((r) => (r.id === row.id ? { ...r, status: 'sending' } : r))
    );

    try {
      const payload = {
        email: row.email,
        title: row.shareKeyTitle || '',
      };

      await updateShareKey({
        projectId,
        tenderId: numericTenderId,
        shareKey: row.id,
        body: payload,
      } as any);

      setRows((prev) =>
        prev.map((r) => {
          if (r.id !== row.id) return r;
          return {
            ...r,
            status: 'success',
            rowState: 'clean',
            originalEmail: r.email,
            originalShareKeyTitle: r.shareKeyTitle,
          };
        })
      );

      message.success(t('invite-share-success', 'Saved'));
      queryCache.invalidateQueries([QueryKeys.tenderAdminSharedTender]);
    } catch (error) {
      appLog('update-share-key-error', { rowId: row.id, error });
      setRows((prev) =>
        prev.map((r) => (r.id === row.id ? { ...r, status: 'error' } : r))
      );
      message.error(t('invite-share-edit-failed', 'Failed to save'));
    } finally {
      setSavingRowIds((prev) => ({ ...prev, [row.id]: false }));
    }
  };

  // add emails into list
  const upsertEmails = (arr: string[]) => {
    const cleaned = arr.map((s) => s.trim()).filter(Boolean);
    if (!cleaned.length) return;

    setRows((prev) => {
      const existingEmails = new Set(
        prev.map((r) => r.email.toLowerCase().trim())
      );
      const nextRows = [...prev];

      // Track emails seen in current batch for within-batch duplicate detection
      const batchEmails = new Set<string>();

      // Process ALL emails, no longer using uniq()
      cleaned.forEach((email) => {
        const normalized = email.toLowerCase();
        const isDuplicateEmail =
          existingEmails.has(normalized) || batchEmails.has(normalized);

        // Always add the email, but mark if it's a duplicate
        const newRow = createInviteRow(email);
        newRow.isDuplicate = isDuplicateEmail;
        nextRows.push(newRow);

        batchEmails.add(normalized);
      });

      return nextRows;
    });
  };

  const handleAddFromInput = () => {
    const parsed = parseEmailsFromText(inputEmails);
    if (!parsed.length) {
      message.info(t('invite-nothing-to-add', 'Nothing to add'));
      return;
    }
    upsertEmails(parsed);
    setInputEmails('');
  };

  const removeRow = (id: string) => {
    setRows((prev) => prev.filter((r) => r.id !== id));
    setSelectedRowIds((prev) => prev.filter((rid) => rid !== id));
  };

  const clearAll = () => {
    setRows([]);
    setInputEmails('');
    setSelectedRowIds([]);
  };

  useEffect(() => {
    if (!projectId || !tenderId) return;
    appLog('share-invitation-load', { projectId, tenderId });
    setIsInvitationDirty(false);
  }, [projectId, tenderId]);

  useEffect(() => {
    if (!isEditResendMode) return;
    if (!initialShares) return;

    const sourceArray = Array.isArray(initialShares)
      ? initialShares
      : [initialShares];

    const mapped = sourceArray.map((share: any) =>
      createRowFromShare(intl, share)
    );
    setRows(mapped);

    // By default select all resendable + valid emails
    const defaultSelected = mapped
      .filter((r) => r.canResend && isEmail(r.email))
      .map((r) => r.id);
    setSelectedRowIds(defaultSelected);
  }, [isEditResendMode, initialShares]);

  const handleSaveInvitation = async () => {
    const values = sharesForm.getValues();

    if (!projectId && !tenderId && !values.title && !values.message) return;

    appLog('share-invitation-save', {
      projectId,
      tenderId,
      title: values.title,
      message: values.message,
      mode,
    });

    setIsInvitationSaving(true);

    await doPostInvitationMessage({
      projectId,
      tenderId,
      body: { title: values.title, description: values.message },
    });

    setIsInvitationEditing(false);
    setIsInvitationDirty(false);
    setIsInvitationSaving(false);
  };

  const handleInvitationEditClick = () => {
    if (!isInvitationEditing) {
      setIsInvitationEditing(true);
      setIsInvitationDirty(false);
      return;
    }

    if (!isInvitationSaving) {
      handleSaveInvitation();
    }
  };

  const runAnimation = async () => {
    if (!canSend) return;
    if (!projectId) {
      message.error('Missing project id');
      return;
    }

    const numericTenderId = Number(tenderId);
    if (!numericTenderId) {
      message.error('Missing tender id');
      return;
    }

    const resendableRows = isEditResendMode
      ? rows.filter(
          (r) =>
            r.canResend && isEmail(r.email) && selectedRowIds.includes(r.id)
        )
      : rows.filter((r) => isEmail(r.email));

    if (!resendableRows.length) {
      message.warning(
        t('invite-no-selected-rows', 'No recipients selected to invite')
      );
      return;
    }

    // in edit-resend mode, prevent sending if selected includes dirty rows
    if (isEditResendMode) {
      const selectedDirty = resendableRows.filter(
        (r) => r.rowState === 'dirty'
      );
      if (selectedDirty.length) {
        message.warning(
          t(
            'invite-save-before-send',
            'Some selected rows have unsaved changes. Please Save them first.'
          )
        );
        return;
      }
    }

    appLog('share-send', {
      mode,
      projectId,
      tenderId: numericTenderId,
      invitationTemplate: {
        title: invitationTitle,
        message: invitationMessage,
      },
      recipients: resendableRows.map((r) => ({
        id: r.id,
        email: r.email,
        title: r.shareKeyTitle,
      })),
    });

    setIsSending(true);

    // Filter rows based on skipDuplicates BEFORE animations
    const rowsToSend = skipDuplicates
      ? resendableRows.filter((row) => !row.isDuplicate)
      : resendableRows;

    const sendingIds = new Set(rowsToSend.map((r) => r.id));

    // idle -> sending animation only for rows we actually send
    rows.forEach((row, i) => {
      if (!sendingIds.has(row.id)) return;
      const tOut = window.setTimeout(
        () =>
          setRows((prev) =>
            prev.map((r) => (r.id === row.id ? { ...r, status: 'sending' } : r))
          ),
        220 * i
      );
      timersRef.current.push((tOut as unknown) as number);
    });

    try {
      if (!isEditResendMode) {
        // Invite mode → POST /share_keys (array)
        const body = rowsToSend.map((row) => ({
          email: row.email,
          title: row.shareKeyTitle || '',
        }));

        await doPostShareKeys(
          {
            projectId,
            tenderId: numericTenderId,
            body,
          } as any,
          {
            // Fix: Move success animation inside onSuccess callback
            onSuccess: () => {
              rowsToSend.forEach((row, i) => {
                const tOut = window.setTimeout(
                  () =>
                    setRows((prev) =>
                      prev.map((r) =>
                        r.id === row.id ? { ...r, status: 'success' } : r
                      )
                    ),
                  1200 + 240 * i
                );
                timersRef.current.push((tOut as unknown) as number);
              });
            },
          }
        );
      } else {
        // EDIT & RESEND MODE → PATCH /share_keys/invite_messages
        const patchBody = rowsToSend.map((row) => ({
          share_key: row.id,
          email: row.email,
          title: row.shareKeyTitle || '',
        }));

        await doPatchInviteMessages(
          {
            projectId,
            tenderId: numericTenderId,
            body: patchBody,
          } as any,
          {
            // Fix: Move success animation inside onSuccess callback
            onSuccess: () => {
              rowsToSend.forEach((row, i) => {
                const tOut = window.setTimeout(
                  () =>
                    setRows((prev) =>
                      prev.map((r) =>
                        r.id === row.id ? { ...r, status: 'success' } : r
                      )
                    ),
                  900 + 160 * i
                );
                timersRef.current.push((tOut as unknown) as number);
              });
            },
          }
        );
      }
    } catch (error) {
      appLog('share-send-error', error);

      // set error for rows we attempted to send
      setRows((prev) =>
        prev.map((r) => (sendingIds.has(r.id) ? { ...r, status: 'error' } : r))
      );
    } finally {
      setIsSending(false);
    }
  };

  // Expose runAnimation to parent via ref or callback
  useEffect(() => {
    // Store the send handler reference
    (window as any).__inviteTabSendHandler = runAnimation;
    return () => {
      delete (window as any).__inviteTabSendHandler;
    };
  }, [runAnimation]);

  useEffect(() => {
    return () => {
      timersRef.current.forEach((t) => clearTimeout(t));
      timersRef.current = [];
    };
  }, []);

  useEffect(() => {
    // Працюємо тільки коли модалка відкрита
    if (!isModalVisible) return;
    if (!shareMainMessage) return;

    const tpl = Array.isArray(shareMainMessage)
      ? shareMainMessage[0]
      : shareMainMessage;

    if (!tpl) return;

    // Якщо юзер зараз редагує і вже щось міняв — не перетираємо
    if (isInvitationEditing && isInvitationDirty) {
      return;
    }

    sharesForm.reset({
      title: tpl.title || '',
      message: tpl.description || '',
    });

    setIsInvitationDirty(false);
  }, [
    isModalVisible,
    shareMainMessage,
    isInvitationEditing,
    isInvitationDirty,
    sharesForm,
  ]);

  const emailCount = rows.length;
  const isInvitationReadOnly =
    !isInvitationEditing ||
    isInvitationLoading ||
    isInvitationSaving ||
    tenderCompleted;

  return (
    <div>
      {tenderCompleted && (
        <div
          css={{
            padding: '12px',
            marginBottom: '16px',
            backgroundColor: '#fff1f0',
            border: '1px solid #ffa39e',
            borderRadius: '4px',
            color: '#cf1322',
            fontSize: '14px',
          }}>
          <FontAwesomeIcon
            icon={['fas', 'exclamation-circle']}
            style={{ marginRight: '8px' }}
          />
          {t('invite-disabled-tender-closed', 'Tender is closed')}
        </div>
      )}
      {/* Recipients */}
      <div css={styles.section}>
        <div css={styles.sectionHeader}>
          <div css={styles.sectionTitle}>
            <FontAwesomeIcon icon={['fas', 'user-tie']} />
            <span>{t('invite-recipients', 'Recipients')}</span>
          </div>

          <div css={styles.headerActions}>
            <Tooltip title={t('invite-clear-all', 'Clear all')}>
              <Button
                size="small"
                onClick={clearAll}
                disabled={!emailCount || isSending}>
                {t('invite-clear', 'Clear')}
              </Button>
            </Tooltip>
          </div>
        </div>

        <div css={styles.listCard}>
          <div css={styles.cardHeader}>
            <FontAwesomeIcon icon={['far', 'envelope']} />
            <span>
              {t('invite-email-list', 'Email list')}{' '}
              <em css={styles.muted}>({emailCount})</em>
            </span>
          </div>

          <div css={styles.listWrap}>
            {emailCount === 0 ? (
              <div css={styles.emptyState}>
                <FontAwesomeIcon icon={['fas', 'inbox']} />
                <span>
                  {t('invite-no-recipients', 'No recipients yet. Add below.')}
                </span>
              </div>
            ) : (
              rows.map((row) => {
                const invalid =
                  !isEmail(row.email) ||
                  row.lockedReason === 'rejected' ||
                  row.lockedReason === 'deleted';
                const st: EmailStatus = row.status || 'idle';
                const meta = STATUS_META[st];

                const badgeClasses = [styles.badge];

                // In INVITE mode, the left dot should reflect send status (idle/sending/success/error)
                if (!isEditResendMode) {
                  if (st === 'error') badgeClasses.push(styles.badgeError);
                  else if (st === 'sending')
                    badgeClasses.push(styles.badgeSending);
                  else if (st === 'success')
                    badgeClasses.push(styles.badgeSuccess);
                  else badgeClasses.push(styles.badgeIdle);
                } else {
                  // Edit-resend mode keeps your original meaning for dot
                  if (invalid) {
                    badgeClasses.push(styles.badgeInvalid);
                  } else if (row.rowState === 'new') {
                    badgeClasses.push(styles.badgeNew);
                  } else if (row.rowState === 'dirty') {
                    badgeClasses.push(styles.badgeDirty);
                  }
                }

                const rowClasses = [styles.row];
                if (invalid) {
                  rowClasses.push(styles.rowInvalid);
                } else if (row.isDuplicate) {
                  rowClasses.push(styles.rowDuplicate);
                }

                const isResendable = row.canResend && !invalid;

                let invalidLabel: string | null = null;
                if (!isEmail(row.email)) {
                  invalidLabel = t('invite-invalid-email', 'Invalid');
                } else if (row.lockedReason === 'rejected') {
                  invalidLabel = t(
                    'invite-share-rejected',
                    'Cannot resend (Rejected)'
                  );
                } else if (row.lockedReason === 'deleted') {
                  invalidLabel = t(
                    'invite-share-deleted',
                    'Cannot resend (Deleted)'
                  );
                } else if (row.isDuplicate) {
                  invalidLabel = t('invite-duplicate-email', 'Duplicate');
                }

                const isSelected = selectedRowIds.includes(row.id);

                const showSave =
                  isEditResendMode &&
                  row.rowState === 'dirty' &&
                  row.canEdit &&
                  !invalid;

                const isRowSaving = !!savingRowIds[row.id];

                return (
                  <div
                    key={row.id}
                    css={rowClasses}
                    style={{
                      gridTemplateColumns: showSave
                        ? '32px 1.6fr 1.4fr 32px 100px'
                        : '32px 1.6fr 1.4fr 32px 70px',
                    }}>
                    <div css={styles.statusDotCell}>
                      {isEditResendMode ? (
                        isResendable ? (
                          <Checkbox
                            checked={isSelected}
                            disabled={
                              isSending || isRowSaving || tenderCompleted
                            }
                            onChange={(e) => {
                              const checked = e.target.checked;
                              setSelectedRowIds((prev) => {
                                if (checked) {
                                  if (prev.includes(row.id)) return prev;
                                  return [...prev, row.id];
                                }
                                return prev.filter((id) => id !== row.id);
                              });
                            }}
                          />
                        ) : row.shareStatus ? (
                          <span css={styles.statusIconWrapper}>
                            {row.shareStatus.icon}
                          </span>
                        ) : (
                          <span css={badgeClasses} />
                        )
                      ) : (
                        <span css={badgeClasses} />
                      )}
                    </div>

                    <div css={styles.emailCell}>
                      <Input
                        size="small"
                        value={row.email}
                        disabled={
                          isSending ||
                          isRowSaving ||
                          !row.canEdit ||
                          tenderCompleted
                        }
                        css={styles.inlineInput}
                        onChange={(e) =>
                          updateRow(row.id, { email: e.target.value })
                        }
                      />
                      {invalidLabel && (
                        <span css={styles.invalidText}>{invalidLabel}</span>
                      )}
                      {isEditResendMode && row.shareStatus && (
                        <span css={styles.shareStatusText}>
                          {row.shareStatus.icon}
                          <span style={{ marginLeft: 4 }}>
                            {row.shareStatus.text}
                          </span>
                        </span>
                      )}
                    </div>

                    <div css={styles.shareKeyTitleCell}>
                      <Input
                        size="small"
                        value={row.shareKeyTitle}
                        disabled={
                          isSending ||
                          isRowSaving ||
                          !row.canEdit ||
                          tenderCompleted
                        }
                        css={styles.inlineInput}
                        placeholder={t(
                          'invite-share-key-title',
                          'Share key title (optional)'
                        )}
                        onChange={(e) =>
                          updateRow(row.id, {
                            shareKeyTitle: e.target.value,
                          })
                        }
                      />
                    </div>

                    <div css={styles.statusIconCell}>
                      <Tooltip
                        title={t('invite-' + meta.tip.toLowerCase(), meta.tip)}>
                        <FontAwesomeIcon
                          icon={meta.icon as any}
                          spin={st === 'sending'}
                          color={meta.color}
                          css={st === 'success' ? styles.successPop : undefined}
                        />
                      </Tooltip>
                    </div>

                    <div css={styles.actionsCell}>
                      {/*  Single action button: toggles Remove <-> Save (no extra whitespace/buttons) */}
                      {showSave ? (
                        <Button
                          size="small"
                          type="primary"
                          style={{ maxWidth: '50px' }}
                          onClick={() => handleSaveRow(row)}
                          loading={isRowSaving}
                          disabled={isSending || tenderCompleted}>
                          {t('invite-save', 'Save')}
                        </Button>
                      ) : (
                        <Button
                          size="small"
                          danger
                          type="link"
                          onClick={() => removeRow(row.id)}
                          disabled={
                            isSending || isRowSaving || tenderCompleted
                          }>
                          {t('invite-remove', 'Remove')}
                        </Button>
                      )}
                    </div>
                  </div>
                );
              })
            )}
          </div>

          {!isEditResendMode && (
            <Fragment>
              <Divider style={{ margin: '12px 0' }} />

              <div css={styles.inlineAdd}>
                <Input
                  value={inputEmails}
                  onChange={(e) => setInputEmails(e.target.value)}
                  onPressEnter={handleAddFromInput}
                  placeholder={t(
                    'invite-add-emails-placeholder',
                    'Type or paste emails (comma, space, semicolon, or newline)'
                  )}
                  disabled={isSending}
                />
                <Button
                  onClick={handleAddFromInput}
                  disabled={!inputEmails.trim() || isSending}
                  type="primary">
                  {t('invite-add-to-list', 'Add to list')}
                </Button>
              </div>

              <div
                css={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginTop: 8,
                }}>
                <div css={styles.helperText}>
                  {t(
                    'invite-email-parse-help',
                    "We'll automatically deduplicate and validate addresses."
                  )}
                </div>
                <Checkbox
                  checked={skipDuplicates}
                  onChange={(e) => setSkipDuplicates(e.target.checked)}
                  disabled={isSending || tenderCompleted}>
                  {t(
                    'invite-skip-duplicates',
                    'Skip emails that already exist'
                  )}
                </Checkbox>
              </div>
            </Fragment>
          )}
        </div>
      </div>

      <Divider />

      {/* Invitation template */}
      <div css={styles.section}>
        <div css={styles.sectionHeader}>
          <div css={styles.sectionTitle}>
            <FontAwesomeIcon icon={['far', 'file-lines']} />
            <span>{t('invite-invitation', 'Invitation')}</span>
          </div>

          <div css={styles.headerActions}>
            <Tooltip
              title={
                isInvitationEditing
                  ? t(
                      'invite-save-invitation-tooltip',
                      'Save the current invitation text'
                    )
                  : t(
                      'invite-edit-invitation-tooltip',
                      'Edit the invitation title and message'
                    )
              }>
              <Button
                size="small"
                onClick={handleInvitationEditClick}
                loading={isInvitationSaving}
                disabled={isInvitationLoading || tenderCompleted}>
                <FontAwesomeIcon
                  icon={
                    isInvitationEditing ? ['fas', 'lock-open'] : ['far', 'lock']
                  }
                  style={{ marginRight: 6 }}
                />
                {isInvitationEditing
                  ? t('invite-save-invitation', 'Save')
                  : t('invite-edit-invitation', 'Edit')}
              </Button>
            </Tooltip>
          </div>
        </div>

        <div style={{ display: 'flex', gap: 24, height: '100%' }}>
          {/* Left panel - Form */}
          <div
            style={{
              flex: 1,
              minWidth: 450,
              display: 'flex',
              flexDirection: 'column',
            }}>
            <div css={styles.letterGrid}>
              <div>
                <div css={styles.fieldLabel}>{t('invite-title', 'Title')}</div>
                <Input
                  value={invitationTitle}
                  maxLength={200}
                  placeholder={t('invite-enter-title', 'Enter title')}
                  disabled={isInvitationReadOnly}
                  onChange={(e) => {
                    sharesForm.setValue('title', e.target.value, {
                      shouldDirty: true,
                    });
                    if (isInvitationEditing) setIsInvitationDirty(true);
                  }}
                />
              </div>

              <div>
                <div css={styles.fieldLabel}>
                  {t('invite-message', 'Message')}
                </div>
                <TextArea
                  value={invitationMessage}
                  autoSize={{ minRows: 5, maxRows: 10 }}
                  placeholder={t('invite-enter-message', 'Message')}
                  disabled={isInvitationReadOnly}
                  onChange={(e) => {
                    sharesForm.setValue('message', e.target.value, {
                      shouldDirty: true,
                    });
                    if (isInvitationEditing) setIsInvitationDirty(true);
                  }}
                />
              </div>

              <div css={styles.noteRow}>
                <FontAwesomeIcon icon={['far', 'info-circle']} />
                <span>
                  {t(
                    'invite-send-requirements',
                    'Need at least 1 valid email, a Title and a Message to send.'
                  )}
                </span>
              </div>
            </div>
          </div>

          {/* Right panel - Preview */}
          <div
            style={{
              flex: 1,
              minWidth: 450,
              display: 'flex',
              flexDirection: 'column',
            }}>
            <div
              style={{
                width: '100%',
                height: '100%',
                background: '#fafafa',
                border: '1px solid #e8e8e8',
                borderRadius: 6,
                padding: 20,
                display: 'flex',
                flexDirection: 'column',
                minHeight: 400,
              }}>
              <Typography.Title
                level={5}
                style={{ marginTop: 0, marginBottom: 16 }}>
                {intl.formatMessage({ id: 'invite-email-preview-title' })}
              </Typography.Title>

              {previewLoading && (
                <div
                  style={{
                    flex: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: 'white',
                    borderRadius: 4,
                  }}>
                  <Spin />
                </div>
              )}

              {!previewLoading && !previewHtml && (
                <div
                  style={{
                    flex: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: 'white',
                    borderRadius: 4,
                  }}>
                  <Empty
                    description={intl.formatMessage({
                      id: 'invite-preview-empty',
                    })}
                  />
                </div>
              )}

              {!previewLoading && previewHtml && (
                <div
                  style={{
                    flex: 1,
                    border: '1px solid #d9d9d9',
                    borderRadius: 4,
                    background: 'white',
                    overflow: 'hidden',
                    display: 'flex',
                  }}>
                  <iframe
                    srcDoc={previewHtml}
                    style={{
                      width: '100%',
                      height: '100%',
                      border: 'none',
                      display: 'block',
                    }}
                    sandbox="allow-same-origin"
                    title="Email Preview"
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const styleFn: ThemedStyleFn = () => ({
  section: { display: 'flex', flexDirection: 'column', gap: 12 },

  sectionHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 6,
  },

  sectionTitle: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    fontWeight: 700,
    fontSize: 16,
  },

  headerActions: { display: 'flex', gap: 8 },

  listCard: {
    border: '1px solid #d9d9d9',
    borderRadius: 8,
    padding: 12,
    background: '#fff',
  },

  cardHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    fontWeight: 600,
    marginBottom: 8,
  },

  listWrap: {
    minHeight: 140,
    maxHeight: 260,
    overflowY: 'auto',
    paddingRight: 6,
  },

  row: {
    display: 'grid',
    gridTemplateColumns: '32px 1.6fr 1.4fr 32px 70px',
    alignItems: 'start',
    gap: 10,
    padding: '10px 10px',
    borderRadius: 8,
    border: '1px solid #f0f0f0',
    marginBottom: 8,
    background: '#fff',
    transition: 'border-color 120ms ease, background 120ms ease',
  },

  rowInvalid: {
    borderColor: '#ff4d4f',
    background: 'rgba(255, 77, 79, 0.06)',
  },

  rowDuplicate: {
    borderColor: '#faad14',
    background: 'rgba(250, 173, 20, 0.06)',
  },

  statusDotCell: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'center',
    paddingTop: 4,
  },

  statusIconCell: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'center',
    minHeight: 28,
    paddingTop: 4,
  },

  statusIconWrapper: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 14,
  },

  actionsCell: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'flex-end',
    gap: 10,
    paddingTop: 0,
  },

  badge: {
    width: 10,
    height: 10,
    borderRadius: 999,
    background: '#52c41a',
  },

  badgeIdle: { background: '#fa8c16' },
  badgeSending: { background: '#1677ff' },
  badgeSuccess: { background: '#52c41a' },
  badgeError: { background: '#ff4d4f' },

  badgeInvalid: { background: '#ff4d4f' },
  badgeNew: { background: '#13c2c2' },
  badgeDirty: { background: '#faad14' },

  emailCell: {
    minWidth: 0,
    color: '#262626',
    fontSize: 13,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-start',
    gap: 2,
  },

  invalidText: {
    fontSize: 12,
    color: '#ff4d4f',
    marginTop: 4,
  },

  shareStatusText: {
    marginTop: 2,
    fontSize: 12,
    color: '#8c8c8c',
    display: 'flex',
    alignItems: 'center',
  },

  shareKeyTitleCell: {
    minWidth: 0,
    display: 'flex',
    alignItems: 'flex-start',
  },

  inlineInput: {
    border: 'none',
    borderBottom: '1px solid #d9d9d9',
    borderRadius: 0,
    padding: '2px 4px 2px 0',
    boxShadow: 'none !important',
    outline: 'none',
    background: 'transparent',

    '&:hover': {
      borderBottomColor: '#bfbfbf',
    },

    '&:focus, &.ant-input-focused': {
      borderBottomColor: '#40a9ff',
      boxShadow: 'none',
    },
  },

  inlineAdd: {
    display: 'grid',
    gridTemplateColumns: '1fr auto',
    gap: 8,
  },

  helperText: { fontSize: 12, color: '#8c8c8c', marginTop: 6 },

  muted: { color: '#8c8c8c', fontStyle: 'normal' },

  emptyState: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    color: '#8c8c8c',
    padding: '8px 6px',
  },

  letterGrid: { display: 'grid', gridTemplateColumns: '1fr', gap: 12 },

  fieldLabel: {
    fontSize: 12,
    fontWeight: 600,
    color: '#595959',
    marginBottom: 4,
  },

  noteRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    fontSize: 12,
    color: '#8c8c8c',
  },

  successPop: {
    animation: 'successPop 220ms ease-out',
    '@keyframes successPop': {
      '0%': { transform: 'scale(0.6)', opacity: 0.3 },
      '70%': { transform: 'scale(1.15)', opacity: 1 },
      '100%': { transform: 'scale(1)', opacity: 1 },
    },
  },
});
